import { LogEvents, logger } from 'lib/common/components/LoggerController';

import {
  getAllActiveConnections,
  getAllActiveThirdPartyConnections,
  isMultiPartyConferenceEnabled,
} from 'lib/common/utils/conferenceConnections';
import connectAction from 'lib/common/utils/connectAction';
import connectGetter from 'lib/common/utils/connectGetter';

import TTask from '../../../types/Task';

function getTaskContact(tasks: TTask[], taskId: string) {
  return tasks.find((task) => task.taskId === taskId)?.contact;
}

export default function useCallActions({ tasks, handleContactChange }) {
  const holdCall = async ({ taskId }: { taskId: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    try {
      // When the initial connection has disconnected we need to fall back to holding the third party connection
      const activeConnection =
        connectGetter(contact, 'getActiveInitialConnection') ||
        connectGetter(contact, 'getSingleActiveThirdPartyConnection');

      if (!activeConnection) {
        throw new Error('No active connection found');
      }

      await connectAction('hold', activeConnection);

      logger.info(LogEvents.CALL_ACTIONS.HOLD.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CALL_ACTIONS.HOLD.FAIL, { contactId: taskId, error });

      return Promise.reject(error);
    }
  };

  const resumeCall = async ({ taskId }: { taskId: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    try {
      // When the initial connection has disconnected we need to fall back to holding the third party connection
      const activeConnection =
        connectGetter(contact, 'getActiveInitialConnection') ||
        connectGetter(contact, 'getSingleActiveThirdPartyConnection');

      if (!activeConnection) {
        throw new Error('No active connection found');
      }

      await connectAction('resume', activeConnection);

      logger.info(LogEvents.CALL_ACTIONS.RESUME.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CALL_ACTIONS.RESUME.FAIL, { contactId: taskId, error });

      return Promise.reject(error);
    }
  };

  const resumeConferenceConnection = async ({ taskId, connectionId }: { taskId: string; connectionId?: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact || !connectionId) {
      return Promise.resolve();
    }

    try {
      // This is a workaround for the connect streams not providing a way to resume a conference connection when
      // isMultiPartyConferenceEnabled is not enabled and the scenario is as follows:
      // 1. Agent is in a conference with 2 participants and one of them is on hold
      // 2. When resuming the connection, it will through an error therefore we need to join the conference instead
      const activeConnections = getAllActiveConnections(contact);
      const isTwoParticipantsConference = activeConnections.length === 2;
      const isOneParticipantOnHold =
        activeConnections.filter(
          (connection) => connectGetter(connection, 'getState')?.type === connect.ConnectionStateType.HOLD
        ).length === 1;
      const isJoinAction = isTwoParticipantsConference && isOneParticipantOnHold;
      const isMultiPartyConference = isMultiPartyConferenceEnabled(contact);

      if (isJoinAction && !isMultiPartyConference) {
        await connectAction('conferenceConnections', contact);

        return logger.info(LogEvents.CONFERENCE_ACTIONS.RESUME.SUCCESS, { contactId: taskId });
      }

      const selectedConnection = getAllActiveConnections(contact, { includeAgent: true }).find(
        (connection) => connection.connectionId === connectionId
      );

      if (!selectedConnection) {
        throw new Error('Something went wrong while trying to resume the call, please try again');
      }

      await connectAction('resume', selectedConnection);

      return logger.info(LogEvents.CONFERENCE_ACTIONS.RESUME.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CONFERENCE_ACTIONS.RESUME.FAIL, { contactId: taskId, error });
    }
  };

  const holdConferenceConnection = async ({ taskId, connectionId }: { taskId: string; connectionId?: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact || !connectionId) {
      return Promise.resolve();
    }

    try {
      const selectedConnection = getAllActiveConnections(contact, { includeAgent: true }).find(
        (connection) => connection.connectionId === connectionId
      );

      if (!selectedConnection) {
        throw new Error('Something went wrong while trying to hold the call, please try again');
      }

      await connectAction('hold', selectedConnection);
      logger.info(LogEvents.CONFERENCE_ACTIONS.HOLD.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CONFERENCE_ACTIONS.HOLD.FAIL, { contactId: taskId, error });
    }
  };

  const endConferenceConnection = async ({ taskId, connectionId }: { taskId: string; connectionId?: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact || !connectionId) {
      return Promise.resolve();
    }

    try {
      const selectedConnection = getAllActiveThirdPartyConnections(contact).find(
        (connection) => connection.connectionId === connectionId
      );

      if (!selectedConnection) {
        throw new Error('Something went wrong while trying to disconnect the participant, please try again');
      }

      await connectAction('destroy', selectedConnection);

      logger.info(LogEvents.CONFERENCE_ACTIONS.END.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CONFERENCE_ACTIONS.END.FAIL, { contactId: taskId, error });
    }
  };

  const muteConnection = async ({ taskId, connectionId }: { taskId: string; connectionId?: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact || !connectionId) {
      return Promise.resolve();
    }

    try {
      const selectedConnection = getAllActiveConnections(contact, { includeAgent: true }).find(
        (connection) => connection.connectionId === connectionId
      );

      if (!selectedConnection) {
        throw new Error('Something went wrong while trying to mute the call, please try again');
      }

      await connectAction('muteParticipant', selectedConnection);
      logger.info(LogEvents.CALL_ACTIONS.MUTE.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CALL_ACTIONS.MUTE.FAIL, { contactId: taskId, error });
    }
  };

  const unmuteConnection = async ({ taskId, connectionId }: { taskId: string; connectionId?: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact || !connectionId) {
      return Promise.resolve();
    }

    try {
      const selectedConnection = getAllActiveConnections(contact, { includeAgent: true }).find(
        (connection) => connection.connectionId === connectionId
      );

      if (!selectedConnection) {
        throw new Error('Something went wrong while trying to unmute the call, please try again');
      }

      await connectAction('unmuteParticipant', selectedConnection);
      logger.info(LogEvents.CALL_ACTIONS.UNMUTE.SUCCESS, { contactId: taskId });
    } catch (error) {
      logger.error(LogEvents.CALL_ACTIONS.UNMUTE.FAIL, { contactId: taskId, error });
    }
  };

  return {
    holdCall,
    resumeCall,
    resumeConferenceConnection,
    holdConferenceConnection,
    endConferenceConnection,
    muteConnection,
    unmuteConnection,
  };
}
